import { makeAutoObservable } from 'mobx'
import { config, locales, source, LocaleId } from '../translations/index'
import { Language, TranslationsInterface } from '../types/translations.generated'
import { RootStore } from './Root.store'
import { devLog } from '@/methods/devLog'

function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override?: Record<string, unknown>
): T {
  if (!override) {
    return base
  }

  const result = { ...base }

  for (const key in override) {
    const baseValue = base[key]
    const overrideValue = override[key]

    if (
      overrideValue !== null &&
      typeof overrideValue === 'object' &&
      !Array.isArray(overrideValue) &&
      baseValue !== null &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key as keyof T] = deepMerge(
        baseValue as Record<string, unknown>,
        overrideValue as Record<string, unknown>
      ) as T[keyof T]
    } else if (overrideValue !== undefined && overrideValue !== '') {
      result[key as keyof T] = overrideValue as T[keyof T]
    }
  }

  return result
}

// Picks the locale, e.g. `de` + enpalTheme -> `de-enpal`.
function resolveLocale(language: string, themeId?: string, configurationId?: string): string {
  if (!themeId) return language

  const override = config.configurationOverrides.find(
    (o) => o.configurationId === configurationId && o.language === language
  )
  if (override) return override.locale

  const client = config.clients[themeId as keyof typeof config.clients]
  const brandLocale = `${language}-${client}`

  return client && brandLocale in config.locales ? brandLocale : language
}

// Merges en <- ...fallback chain <- locale. Unknown locales get plain English, and "" never
// overrides a fallback value.
function resolveTranslations(locale: string): TranslationsInterface {
  if (!(locale in config.locales)) return source

  const id = locale as LocaleId
  const chain = [id, ...(config.locales[id] as LocaleId[])]
    .filter((l) => l !== config.source)
    .reverse()

  return chain.reduce(
    (result, l) => deepMerge(result, locales[l] as Record<string, unknown>),
    source as unknown as Record<string, unknown>
  ) as unknown as TranslationsInterface
}

export class TranslationsStore {
  private rootStore: RootStore
  translations: TranslationsInterface = source
  language = 'en'

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  setTranslations(baseLanguage: Language, configurationId?: string): void {
    this.language = baseLanguage.substring(0, 2)

    const locale = resolveLocale(
      this.language,
      this.rootStore.InterfaceState.themeId,
      configurationId
    )

    devLog('Resolved translation locale: ', locale)

    document.documentElement.lang = this.language
    this.translations = resolveTranslations(locale)
  }
}
